/*
 * The full-size picture, shown over the page while its thumbnail is hovered.
 *
 * NOT A <dialog>. The obvious reading of "as a modal" is the Modal component,
 * and it is the wrong tool for a hover: showModal() makes the rest of the page
 * inert, so the thumbnail underneath would never receive the pointerleave that
 * closes this again - the overlay would stick, permanently - and it would move
 * focus on every mouse-over. What is borrowed instead is the LOOK: the dimming
 * here is the same as .modal::backdrop.
 *
 * pointer-events: none is therefore load-bearing rather than tidiness. The
 * overlay is centred, so it covers the very thumbnail being hovered; if it could
 * receive the pointer, entering it would read as leaving the thumbnail and the
 * preview would flicker off the instant it appeared.
 *
 * Portalled to document.body so that neither a transformed ancestor (which would
 * make `position: fixed` relative to that ancestor instead of the viewport) nor a
 * stacking context between here and the root can affect where it lands. Nothing
 * in the current stylesheet does either, and this does not depend on that staying
 * true.
 *
 * COSTS NO FETCH. The src is the object URL the thumbnail is already using: the
 * list has no separate thumbnail bytes, so the picture drawn at 50px in the row
 * is this same full-resolution image. Hovering displays what has already been
 * downloaded.
 */

import { createPortal } from 'react-dom';

export function ImageHoverPreview({ src }: { src: string }): JSX.Element {
  return createPortal(
    /*
     * aria-hidden: this is a pointer-only affordance that duplicates nothing -
     * the file name is announced from the row itself - so it has nothing to say
     * to a screen reader, and announcing an image with no alt text would be
     * worse than silence.
     */
    <div className="image-preview-overlay" aria-hidden="true">
      <div className="image-preview-frame">
        <img src={src} alt="" />
      </div>
    </div>,
    document.body,
  );
}
